/**
 * Developer handles for a run (docs/ux/review/dev-power-user.md #2): branch, worktree path,
 * commit SHA, "open in editor" URLs and the terminal line that resumes the session. Pure, so
 * the session `⋯` menu, the header chips and the ⌘K palette offer the same actions.
 */
import { resumeCommandLine } from '@viko/protocol'
import type { RunSummary } from './types.js'

export type EditorId = 'vscode' | 'cursor'

/** Per-device override: a URL template with `{path}` (e.g. `zed://file{path}`), or an editor id. */
export const EDITOR_PREF_KEY = 'viko.editorUrl'

export const EDITOR_LABEL: Record<EditorId, string> = { vscode: 'VS Code', cursor: 'Cursor' }

/** Only absolute paths open in an editor (`~/…` cannot be resolved on this device). */
export function isAbsolutePath(p: string | undefined): p is string {
  return !!p && (p.startsWith('/') || /^[A-Za-z]:[\\/]/.test(p))
}

/** The run's checkout on its machine, preferring an absolute path. */
export function runPath(run: Pick<RunSummary, 'cwd' | 'worktree'>): string | undefined {
  if (isAbsolutePath(run.cwd)) return run.cwd
  if (isAbsolutePath(run.worktree)) return run.worktree
  return run.worktree ?? run.cwd
}

/** `vscode://file/abs/path`, `cursor://file/abs/path`, or a custom `{path}` template. */
export function editorUrl(editor: EditorId | string, path: string): string | undefined {
  if (!isAbsolutePath(path)) return undefined
  const norm = path.replace(/\\/g, '/')
  const withSlash = norm.startsWith('/') ? norm : `/${norm}`
  const encoded = withSlash.split('/').map(encodeURIComponent).join('/')
  if (editor === 'vscode') return `vscode://file${encoded}`
  if (editor === 'cursor') return `cursor://file${encoded}`
  if (!editor.includes('{path}')) return undefined
  const url = editor.replace('{path}', encoded)
  // Only app URL schemes: never javascript:, data: or a web page.
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(url) && !/^(https?|javascript|data|file):/i.test(url)
    ? url
    : undefined
}

export function readEditorPref(): string | undefined {
  try {
    return localStorage.getItem(EDITOR_PREF_KEY)?.trim() || undefined
  } catch {
    return undefined
  }
}

/** The shell line that picks this session up in a terminal on its machine. */
export function resumeLine(
  run: Pick<RunSummary, 'agent' | 'nativeSessionId' | 'cwd' | 'worktree' | 'resumeCommand'>,
): string | undefined {
  if (run.resumeCommand) return run.resumeCommand
  if (!run.nativeSessionId) return undefined
  const cwd = runPath(run)
  return resumeCommandLine({
    agent: run.agent === 'codex' ? 'codex' : 'claude',
    nativeSessionId: run.nativeSessionId,
    ...(isAbsolutePath(cwd) ? { cwd } : {}),
  })
}

export interface DevHandle {
  id: 'branch' | 'path' | 'sha' | 'resume' | 'cli' | 'editor' | `editor:${string}`
  label: string
  /** Copy this text… */
  copy?: string
  /** …or open this URL (editor schemes). */
  href?: string
  hint?: string
}

export function devHandles(
  run: Pick<
    RunSummary,
    'agent' | 'branch' | 'gitBranch' | 'worktree' | 'cwd' | 'nativeSessionId' | 'resumeCommand'
  >,
  opts: {
    sha?: string
    editorPref?: string
    /** viko-cli: the matching `viko` command for this session. */
    runId?: string
    live?: boolean
    canLand?: boolean
  } = {},
): DevHandle[] {
  const out: DevHandle[] = []
  const branch = run.branch ?? run.gitBranch
  if (branch) out.push({ id: 'branch', label: 'Copy branch', copy: branch, hint: branch })
  const path = runPath(run)
  if (path) out.push({ id: 'path', label: 'Copy worktree path', copy: path, hint: path })
  if (opts.sha)
    out.push({ id: 'sha', label: 'Copy commit SHA', copy: opts.sha, hint: opts.sha.slice(0, 12) })
  if (path && isAbsolutePath(path)) {
    const custom = opts.editorPref
    if (custom && custom !== 'vscode' && custom !== 'cursor') {
      const href = editorUrl(custom, path)
      if (href) out.push({ id: 'editor', label: 'Open in editor', href })
    }
    const first: EditorId = custom === 'cursor' ? 'cursor' : 'vscode'
    for (const e of [first, first === 'vscode' ? 'cursor' : 'vscode'] as EditorId[]) {
      const href = editorUrl(e, path)
      if (href) out.push({ id: `editor:${e}`, label: `Open in ${EDITOR_LABEL[e]}`, href })
    }
  }
  const resume = resumeLine(run)
  if (resume) out.push({ id: 'resume', label: 'Copy resume command', copy: resume, hint: resume })
  // ---- viko-cli: the same action from a terminal ----
  if (opts.runId) {
    const cli = vikoCommand(opts.runId, opts)
    out.push({ id: 'cli', label: 'Copy viko command', copy: cli, hint: cli })
  }
  // ---- end viko-cli ----
  return out
}

/** The `viko` CLI line for a session: land it when it can land, follow it while live, else logs. */
export function vikoCommand(runId: string, o: { live?: boolean; canLand?: boolean } = {}): string {
  const id = /^[A-Za-z0-9_.:-]+$/.test(runId) ? runId : `'${runId.replace(/'/g, `'\\''`)}'`
  if (o.canLand) return `viko land ${id}`
  return o.live ? `viko tail ${id}` : `viko logs ${id}`
}

/** Copies `text`; false when the clipboard is unavailable or does not answer (permission). */
export async function copyText(text: string, timeoutMs = 1500): Promise<boolean> {
  try {
    return await Promise.race([
      navigator.clipboard.writeText(text).then(() => true),
      new Promise<boolean>((ok) => setTimeout(() => ok(false), timeoutMs)),
    ])
  } catch {
    return false
  }
}
